import { useTranslation } from 'react-i18next';
import { Button, cn, Flex, Icon, Text } from '@kognitos/lattice';

/**
 * Props for RainCheckpointCard.
 */
export interface IRainCheckpointCardProps {
  /**
   * - `approval` — a bare approve/reject gate with nothing to review: the
   *   decision buttons render inline in the chat.
   * - `trigger` — the checkpoint carries facts: a button that opens the
   *   approval dialog where those facts are reviewed.
   * - `resolved` — read-only marker shown once the decision was submitted.
   */
  variant: 'approval' | 'trigger' | 'resolved';
  /** Number of facts under review — shown on the trigger/resolved card. */
  factCount?: number;
  /** Opens the approval dialog. `trigger` variant only. */
  onOpen?: () => void;
  /** Approve the checkpoint. `approval` variant only. */
  onApprove?: () => void;
  /** Reject the checkpoint. `approval` variant only. */
  onReject?: () => void;
}

/**
 * Trigger / decision / marker card for a verify_checkpoint `request_review` in
 * the rain chat. Unlike `RainReviewCard` (the IDP "Provide missing values"
 * trigger), a checkpoint is an approve/reject gate, so this renders the decision
 * directly (inline buttons, or a button into the approval dialog) rather than
 * the IDP document renderer. Kept rain-local to avoid a rain → automation-run
 * import.
 */
export function RainCheckpointCard({
  variant,
  factCount,
  onOpen,
  onApprove,
  onReject,
}: IRainCheckpointCardProps) {
  const { t } = useTranslation('outputs');

  if (variant === 'resolved') {
    return (
      <div
        className="bg-card rounded-lg p-4 border border-border"
        data-testid="rain-checkpoint-card"
        data-variant="resolved"
      >
        <Flex direction="row" align="center" gap={2}>
          <div className="p-2 rounded-md bg-success/10">
            <Icon type="CircleCheck" size="sm" className="text-success" />
          </div>
          <Text weight="medium" level="small">
            {t('outputs.idp.checkpoint.submitted')}
          </Text>
        </Flex>
      </div>
    );
  }

  if (variant === 'approval') {
    return (
      <div
        className="bg-card rounded-lg p-4 border border-border"
        data-testid="rain-checkpoint-card"
        data-variant="approval"
      >
        <Flex direction="row" align="center" justify="end" gap={2}>
          <Button
            variant="outline"
            onClick={onReject}
            data-testid="rain-checkpoint-reject"
          >
            {t('outputs.idp.checkpoint.reject')}
          </Button>
          <Button
            variant="default"
            onClick={onApprove}
            data-testid="rain-checkpoint-approve"
          >
            {t('outputs.idp.checkpoint.approve')}
          </Button>
        </Flex>
      </div>
    );
  }

  // trigger — a DS Button so Enter/Space activation, focus ring, and role come
  // for free.
  return (
    <Button
      variant="ghost"
      onClick={onOpen}
      aria-label={t('outputs.idp.checkpoint.reviewAction')}
      data-testid="rain-checkpoint-card"
      data-variant="trigger"
      className={cn(
        'h-auto w-full justify-between gap-3 rounded-lg border border-border bg-card p-4 text-left',
        'hover:bg-accent/50',
      )}
    >
      <Flex direction="row" align="center" gap={2}>
        <div className="p-2 rounded-md bg-primary/10">
          <Icon type="List" size="sm" className="text-primary" />
        </div>
        <Flex direction="column" gap={0} align="start">
          <Text weight="medium" level="small">
            {t('outputs.idp.checkpoint.reviewAction')}
          </Text>
          {factCount != null && factCount > 0 ? (
            <Text color="muted" level="small">
              {factCount}{' '}
              {factCount === 1
                ? t('outputs.idp.checkpoint.itemSingular')
                : t('outputs.idp.checkpoint.itemPlural')}
            </Text>
          ) : null}
        </Flex>
      </Flex>
      <Icon type="ChevronRight" size="sm" className="text-muted-foreground" />
    </Button>
  );
}
